import { makeStyles, tokens, shorthands } from '@fluentui/react-components';
import { fontScale } from '@/theme';
import { mq } from '../../../theme/scalingTokens';

export const useSpaceCardStyles = makeStyles({
  root: {
    ...shorthands.border('0.0625rem', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius('1rem'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.overflow('hidden'),
    transition: 'box-shadow 0.2s ease',
    boxShadow: tokens.shadow4,
    ':hover': {
      boxShadow: tokens.shadow16,
    },
  },

  /**
   * Figma 2026 SpaceCard (node `1-1269`): white surface with neutral 1 px
   * border, 8 px radius, no shadow until hover. Recommended badge styling
   * comes from the existing `isRecommended` prop combined with
   * `SpaceStatusBadge.figmaRecommended` at the consumer.
   */
  rootFigma: {
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius('0.5rem'),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: 'none',
    ':hover': {
      boxShadow: tokens.shadow4,
    },
  },

  /** Figma 2026 selected outline: navy accent via primary action border. */
  selectedFigma: {
    ...shorthands.borderColor(tokens.colorBrandBackground),
    boxShadow: tokens.shadow4,
  },

  selected: {
    ...shorthands.borderColor(tokens.colorBrandStroke1),
    boxShadow: tokens.shadow16,
  },

  topRow: {
    display: 'flex',
    ...shorthands.padding(
      tokens.spacingVerticalM,
      tokens.spacingHorizontalM,
      tokens.spacingVerticalM,
      tokens.spacingHorizontalM
    ),
    gap: tokens.spacingHorizontalL,
    [mq.tablet]: {
      flexDirection: 'column',
    },
  },

  imageSection: {
    position: 'relative',
    width: '100%',
    height: '100%',
    ...shorthands.borderRadius('1rem'),
    ...shorthands.overflow('hidden'),
  },

  imageSectionWrapper: {
    position: 'relative',
    maxWidth: '14.3125rem',
    flexBasis: '30%',
    flexShrink: 0,
    minHeight: '12.0625rem',
    [mq.tablet]: {
      maxWidth: '100%',
      flexBasis: 'auto',
      width: '100%',
      height: '10rem',
      minHeight: 'unset',
    },
  },

  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
    display: 'block',
  },

  gradientOverlay: {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '5.4375rem',
    background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.7) 0%, rgba(217, 217, 217, 0) 98%)',
    pointerEvents: 'none',
  },

  badgeContainer: {
    position: 'absolute',
    top: tokens.spacingVerticalM,
    left: tokens.spacingHorizontalM,
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    zIndex: 1,
  },

  statusBadge: {
    position: 'absolute',
    top: tokens.spacingVerticalM,
    right: tokens.spacingHorizontalM,
    zIndex: 1,
  },

  contentSection: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.padding(
      tokens.spacingVerticalS,
      tokens.spacingHorizontalXS,
      tokens.spacingVerticalM,
      tokens.spacingHorizontalXS
    ),
    rowGap: tokens.spacingVerticalXS,
    minWidth: 0,
    [mq.mobile]: {
      ...shorthands.padding(tokens.spacingVerticalM),
      rowGap: tokens.spacingVerticalSNudge,
    },
  },

  name: {
    fontSize: fontScale.cardtitle,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    lineHeight: tokens.lineHeightBase400,
    wordBreak: 'break-word',
  },

  metadataRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalSNudge,
    fontSize: fontScale.body,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground2,
  },

  metadataItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalSNudge,
  },

  metadataSeparator: {
    ...shorthands.padding('0', tokens.spacingHorizontalS),
    color: tokens.colorNeutralForeground3,
  },

  amenityRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
    rowGap: tokens.spacingVerticalXS,
    marginTop: tokens.spacingVerticalM,
  },

  collapsibleHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    fontSize: fontScale.body,
    color: tokens.colorBrandForeground1,
    cursor: 'pointer',
    background: 'none',
    ...shorthands.border('0'),
    ...shorthands.padding('0'),
    userSelect: 'none',
  },

  collapsibleHeaderDisabled: {
    color: tokens.colorNeutralForegroundDisabled,
    cursor: 'not-allowed',
  },

  chevronIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    transition: 'transform 0.2s ease',
  },

  chevronExpanded: {
    transform: 'rotate(90deg)',
  },

  buttonArea: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 'auto',
    paddingTop: tokens.spacingVerticalXL,
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
    [mq.mobile]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      paddingTop: tokens.spacingVerticalM,
    },
  },

  selectButton: {
    fontSize: fontScale.body,
    fontWeight: tokens.fontWeightSemibold,
  },

  expandContainer: {
    maxHeight: '0',
    ...shorthands.overflow('hidden'),
    transition: 'max-height 0.3s ease',
  },

  expandContainerOpen: {
    maxHeight: '31.25rem',
  },

  expandedArea: {
    ...shorthands.padding(tokens.spacingVerticalM),
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderRadius('0', '0', '1rem', '1rem'),
  },

  instructionText: {
    fontSize: fontScale.body,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
    marginBottom: tokens.spacingVerticalM,
  },

  preferencesGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalM,
  },

  warningBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontSize: fontScale.caption,
    color: tokens.colorNeutralForeground1,
    backgroundColor: tokens.colorPaletteRedBackground1,
    borderLeft: `${tokens.strokeWidthThick} solid ${tokens.colorPaletteRedBorder1}`,
    borderRadius: tokens.borderRadiusMedium,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalM,
  },

  warningIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    color: tokens.colorPaletteRedForeground1,
    flexShrink: 0,
  },
});
